import { STRUCTURE_GRID_SIZE } from 'data/constants';
import { FootprintPolygonsType, PositionType } from 'data/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  computeFootprintGridBounds,
  computeScaleRatio,
  distanceFromCenter,
  FOOTPRINT_SCALING_CANVAS_OFFSET,
  FootprintScalingDragMode,
  FootprintScalingTransform,
  getCornerCursor,
  gridPointToCanvasPixel,
  SCALE_CORNERS,
  ScaleCorner
} from 'utils/footprint-scaling-gesture';

const CORNER_HANDLE_SIZE = 10;

type FootprintScalingLayerProps = Readonly<{
  polygons: FootprintPolygonsType;
  onTransformChange: (transform: FootprintScalingTransform) => void;
  onTransformEnd: () => void;
  zoom: number;
}>;

export default function FootprintScalingLayer({
  polygons,
  onTransformChange,
  onTransformEnd,
  zoom
}: FootprintScalingLayerProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [dragMode, setDragMode] = useState<FootprintScalingDragMode>('none');
  const [activeCorner, setActiveCorner] = useState<ScaleCorner | null>(null);
  const dragStartGridRef = useRef<PositionType | null>(null);
  const initialCenterRef = useRef<PositionType | null>(null);
  const initialDistanceRef = useRef<number>(0);

  const safeZoom = zoom > 0 ? zoom : 1;

  const screenToGrid = useCallback(
    (clientX: number, clientY: number): PositionType => {
      const svg = svgRef.current;
      if (!svg) return { x: 0, y: 0 };
      const rect = svg.getBoundingClientRect();
      const x = (clientX - rect.left) / safeZoom;
      const y = (clientY - rect.top) / safeZoom;
      return {
        x: (x - FOOTPRINT_SCALING_CANVAS_OFFSET) / STRUCTURE_GRID_SIZE,
        y: (y - FOOTPRINT_SCALING_CANVAS_OFFSET) / STRUCTURE_GRID_SIZE
      };
    },
    [safeZoom]
  );

  const validPolygons = useMemo(
    () => polygons.filter((polygon) => polygon.length >= 3),
    [polygons]
  );
  const points = useMemo(() => validPolygons.flat(), [validPolygons]);

  const bbox = useMemo(() => computeFootprintGridBounds(points), [points]);

  const closedPathD = useMemo(
    () =>
      validPolygons
        .map((polygon) => {
          const pixelPoints = polygon.map((p) => gridPointToCanvasPixel(p));
          return `${pixelPoints
            .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
            .join(' ')} Z`;
        })
        .join(' '),
    [validPolygons]
  );

  const handleCornerMouseDown = useCallback(
    (e: React.MouseEvent, corner: ScaleCorner) => {
      if (e.button !== 0) return;
      if (!bbox) return;
      e.stopPropagation();
      e.preventDefault();
      const center: PositionType = { x: bbox.centerX, y: bbox.centerY };
      const startGrid = screenToGrid(e.clientX, e.clientY);
      initialCenterRef.current = center;
      initialDistanceRef.current = distanceFromCenter(startGrid, center);
      dragStartGridRef.current = startGrid;
      setActiveCorner(corner);
      setDragMode('scale');
    },
    [bbox, screenToGrid]
  );

  const handlePolygonMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      if (!bbox) return;
      e.stopPropagation();
      e.preventDefault();
      const center: PositionType = { x: bbox.centerX, y: bbox.centerY };
      const startGrid = screenToGrid(e.clientX, e.clientY);
      initialCenterRef.current = center;
      dragStartGridRef.current = startGrid;
      setDragMode('translate');
    },
    [bbox, screenToGrid]
  );

  useEffect(() => {
    if (dragMode === 'none') return undefined;

    const handleMouseMove = (e: MouseEvent) => {
      if (!initialCenterRef.current || !dragStartGridRef.current) return;
      const center = initialCenterRef.current;

      if (dragMode === 'scale') {
        const currentGrid = screenToGrid(e.clientX, e.clientY);
        const currentDistance = distanceFromCenter(currentGrid, center);
        const ratio = computeScaleRatio(
          initialDistanceRef.current,
          currentDistance
        );
        if (!Number.isFinite(ratio) || ratio <= 0) return;
        onTransformChange({ ratio, dx: 0, dy: 0, center });
        return;
      }

      if (dragMode === 'translate') {
        const currentGrid = screenToGrid(e.clientX, e.clientY);
        const dx = currentGrid.x - dragStartGridRef.current.x;
        const dy = currentGrid.y - dragStartGridRef.current.y;
        onTransformChange({ ratio: 1, dx, dy, center });
      }
    };

    const handleMouseUp = () => {
      setDragMode('none');
      setActiveCorner(null);
      dragStartGridRef.current = null;
      initialCenterRef.current = null;
      initialDistanceRef.current = 0;
      onTransformEnd();
    };

    globalThis.addEventListener('mousemove', handleMouseMove);
    globalThis.addEventListener('mouseup', handleMouseUp);
    return () => {
      globalThis.removeEventListener('mousemove', handleMouseMove);
      globalThis.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragMode, screenToGrid, onTransformChange, onTransformEnd]);

  if (validPolygons.length === 0 || !bbox) {
    return null;
  }

  const bboxPixelTL = gridPointToCanvasPixel({ x: bbox.minX, y: bbox.minY });
  const bboxPixelBR = gridPointToCanvasPixel({ x: bbox.maxX, y: bbox.maxY });
  const bboxWidth = bboxPixelBR.x - bboxPixelTL.x;
  const bboxHeight = bboxPixelBR.y - bboxPixelTL.y;

  const cornerHandleSize = CORNER_HANDLE_SIZE / safeZoom;
  const cornerHalf = cornerHandleSize / 2;

  const cornerPositions: Record<ScaleCorner, { x: number; y: number }> = {
    nw: { x: bboxPixelTL.x, y: bboxPixelTL.y },
    ne: { x: bboxPixelBR.x, y: bboxPixelTL.y },
    sw: { x: bboxPixelTL.x, y: bboxPixelBR.y },
    se: { x: bboxPixelBR.x, y: bboxPixelBR.y }
  };

  const polygonCursor = dragMode === 'translate' ? 'grabbing' : 'grab';

  return (
    <svg
      ref={svgRef}
      className='absolute z-[25]'
      style={{
        left: `-${FOOTPRINT_SCALING_CANVAS_OFFSET}px`,
        top: `-${FOOTPRINT_SCALING_CANVAS_OFFSET}px`,
        width: `${FOOTPRINT_SCALING_CANVAS_OFFSET * 2}px`,
        height: `${FOOTPRINT_SCALING_CANVAS_OFFSET * 2}px`,
        overflow: 'visible',
        pointerEvents: 'auto'
      }}
    >
      <path
        d={closedPathD}
        fill='rgba(0, 0, 0, 0.03)'
        stroke='#000000'
        strokeWidth={4}
        strokeLinecap='round'
        strokeLinejoin='round'
        style={{
          pointerEvents: 'auto',
          cursor: polygonCursor
        }}
        onMouseDown={handlePolygonMouseDown}
      />

      <rect
        x={bboxPixelTL.x}
        y={bboxPixelTL.y}
        width={bboxWidth}
        height={bboxHeight}
        fill='none'
        stroke='#3b82f6'
        strokeWidth={2 / safeZoom}
        strokeDasharray={`${6 / safeZoom} ${4 / safeZoom}`}
        style={{ pointerEvents: 'none' }}
      />

      {SCALE_CORNERS.map((corner) => {
        const pos = cornerPositions[corner];
        const isActive = activeCorner === corner;
        return (
          <rect
            key={corner}
            x={pos.x - cornerHalf}
            y={pos.y - cornerHalf}
            width={cornerHandleSize}
            height={cornerHandleSize}
            fill={isActive ? '#1d4ed8' : '#3b82f6'}
            stroke='#ffffff'
            strokeWidth={2 / safeZoom}
            style={{ cursor: getCornerCursor(corner) }}
            onMouseDown={(e) => handleCornerMouseDown(e, corner)}
          />
        );
      })}
    </svg>
  );
}
